import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { parseEnvNumberList } from '../../../shared/utils/envParse';
import {
  getAssignedRequests,
  getUserBatches,
  getUserWorkflows,
} from '../../mt-qc-task-001/api/qcTaskApi';
import {
  QC_TASK_ASSIGNMENTS_STALE_MS,
  QC_TASK_BATCHES_STALE_MS,
  QC_TASK_DEFAULT_PAGE_SIZE_OPTIONS,
  QC_TASK_FALLBACK_DEFAULT_PAGE_SIZE,
  QC_TASK_WORKFLOWS_STALE_MS,
} from '../../mt-qc-task-001/constants/qcTaskPageConstants';
import { buildTableFiltersFromSearchParams } from '../../mt-qc-task-001/utils/qcTaskUrlUtils';
import { reqStatusFromReportTab } from '../utils/qcTaskReportTabUtils';
import { normalizeReportTabFromSearchParam } from '../utils/qcTaskReportUrlUtils';

const QC_TASK_FILTER_PARAM_KEYS = [
  'language',
  'district',
  'state',
  'duration',
  'file_name',
  'assigned_to',
  'assigned_reviewer',
];

function readDefaultPageSizeFromEnv() {
  const n = Number(import.meta.env.VITE_QC_TASK_DEFAULT_PAGE_SIZE);
  return Number.isFinite(n) && n > 0 ? n : QC_TASK_FALLBACK_DEFAULT_PAGE_SIZE;
}

/**
 * State + React Query for MT-QC-TASK-REPORT-001 (Approved / Rejected tabs, `req_status` API).
 */
export function useQcTaskReportsPageController() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState(() =>
    normalizeReportTabFromSearchParam(searchParams.get('tab')),
  );
  const [workflowId, setWorkflowId] = useState(
    () => searchParams.get('workflowId') ?? '',
  );
  const [batchId, setBatchId] = useState(
    () => searchParams.get('batchId') ?? '',
  );
  const [pagination, setPagination] = useState(() => ({
    pageIndex: 0,
    pageSize: readDefaultPageSizeFromEnv(),
  }));

  /** MRT sorting state (server-side). */
  const [sorting, setSorting] = useState([]);

  // Backend expects `sortBy` + `sortDirection` (ASC/DESC).
  const sortBy = sorting?.[0]?.id ?? 'file_name';
  const sortDirection = sorting?.[0]?.desc ? 'DESC' : 'ASC';

  const pageSizeOptions = useMemo(
    () =>
      parseEnvNumberList(
        import.meta.env.VITE_QC_TASK_PAGE_SIZES,
        QC_TASK_DEFAULT_PAGE_SIZE_OPTIONS,
      ),
    [],
  );

  const defaultPageSize = useMemo(() => readDefaultPageSizeFromEnv(), []);

  const activeReqStatus = useMemo(
    () => reqStatusFromReportTab(activeTab),
    [activeTab],
  );

  const tableFilters = useMemo(
    () => buildTableFiltersFromSearchParams(searchParams),
    [searchParams],
  );

  const applyTableFiltersToUrl = useCallback(
    (nextFilters) => {
      const next = new URLSearchParams(searchParams);
      QC_TASK_FILTER_PARAM_KEYS.forEach((k) => next.delete(k));

      Object.entries(nextFilters ?? {}).forEach(([k, v]) => {
        if (!QC_TASK_FILTER_PARAM_KEYS.includes(k)) return;
        const s = v == null ? '' : String(v).trim();
        if (!s) return;
        next.set(k, s);
      });

      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const {
    data: workflowsApiData = [],
    isLoading: isWorkflowLoading,
    isFetching: isWorkflowFetching,
    error: workflowsError,
  } = useQuery({
    queryKey: ['qcReportsUserWorkflows', activeReqStatus],
    queryFn: () => getUserWorkflows(activeReqStatus),
    enabled: Boolean(activeReqStatus),
    staleTime: QC_TASK_WORKFLOWS_STALE_MS,
    gcTime: QC_TASK_WORKFLOWS_STALE_MS * 2,
  });

  const workflowOptions = useMemo(
    () => (Array.isArray(workflowsApiData) ? workflowsApiData : []),
    [workflowsApiData],
  );

  const {
    data: batchesApiData = [],
    isLoading: isBatchesLoading,
    isFetching: isBatchesFetching,
    isSuccess: isBatchesSuccess,
    error: batchesError,
  } = useQuery({
    queryKey: ['qcReportsUserBatches', workflowId, activeReqStatus],
    queryFn: () => getUserBatches({ workflowId, reqStatus: activeReqStatus }),
    enabled: Boolean(workflowId && activeReqStatus),
    staleTime: QC_TASK_BATCHES_STALE_MS,
    gcTime: QC_TASK_BATCHES_STALE_MS * 2,
  });

  const batchOptions = useMemo(() => {
    if (!workflowId) return [];
    return Array.isArray(batchesApiData) ? batchesApiData : [];
  }, [batchesApiData, workflowId]);

  const {
    data: assignmentsPayload,
    isLoading: isAssignmentsLoading,
    isFetching: isAssignmentsFetching,
    refetch: refetchAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: [
      'qcReportsAssignedRequests',
      activeReqStatus,
      workflowId,
      batchId,
      pagination.pageIndex,
      pagination.pageSize,
      tableFilters,
      sortBy,
      sortDirection,
    ],
    queryFn: () =>
      getAssignedRequests({
        reqStatus: activeReqStatus,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        batchId,
        workflowId,
        filters: tableFilters,
        sortBy,
        sortDirection,
      }),
    enabled: Boolean(activeReqStatus && workflowId && batchId),
    staleTime: QC_TASK_ASSIGNMENTS_STALE_MS,
    gcTime: QC_TASK_ASSIGNMENTS_STALE_MS * 2,
  });

  const handleSortingChange = useCallback(
    (updater) => {
      setSorting((prev) =>
        typeof updater === 'function' ? updater(prev) : updater,
      );
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    },
    [setPagination],
  );

  const urlQueryKey = searchParams.toString();
  useEffect(() => {
    const params = new URLSearchParams(urlQueryKey);
    const t = normalizeReportTabFromSearchParam(params.get('tab'));
    const w = params.get('workflowId') ?? '';
    const b = params.get('batchId') ?? '';
    setActiveTab((cur) => (cur === t ? cur : t));
    setWorkflowId((cur) => (cur === w ? cur : w));
    setBatchId((cur) => (cur === b ? cur : b));
  }, [urlQueryKey]);

  useEffect(() => {
    if (workflowOptions.length > 0 && !workflowId) {
      setWorkflowId(workflowOptions[0]?.workflow_id ?? '');
    }
  }, [workflowOptions, workflowId]);

  useEffect(() => {
    if (isBatchesSuccess && batchOptions.length > 0 && !batchId) {
      setBatchId(batchOptions[0]?.batch_id ?? '');
    }
  }, [batchOptions, isBatchesSuccess, batchId]);

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [activeTab, workflowId, batchId]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set('tab', activeTab);
    if (workflowId) next.set('workflowId', workflowId);
    if (batchId) next.set('batchId', batchId);
    Object.entries(tableFilters).forEach(([key, value]) => {
      if (value) next.set(key, value);
    });
    const current = searchParams.toString();
    const upcoming = next.toString();
    if (current !== upcoming) {
      setSearchParams(next, { replace: true });
    }
  }, [
    activeTab,
    workflowId,
    batchId,
    tableFilters,
    searchParams,
    setSearchParams,
  ]);

  const handleTabChange = useCallback((nextTab) => {
    setActiveTab(nextTab);
    setWorkflowId('');
    setBatchId('');
  }, []);

  const handleWorkflowChange = useCallback((newValue) => {
    setWorkflowId(newValue?.workflow_id ?? '');
    setBatchId('');
  }, []);

  const handleBatchChange = useCallback((newValue) => {
    setBatchId(newValue?.batch_id ?? '');
  }, []);

  const assignmentsQueryEnabled = Boolean(
    activeReqStatus && workflowId && batchId,
  );

  const rows = assignmentsQueryEnabled ? (assignmentsPayload?.rows ?? []) : [];
  const rowCount = assignmentsQueryEnabled
    ? (assignmentsPayload?.totalCount ?? 0)
    : 0;

  const workflowValue =
    workflowOptions.find((w) => w.workflow_id === workflowId) ?? null;
  const batchValue = batchOptions.find((b) => b.batch_id === batchId) ?? null;

  const errorMessage =
    workflowsError?.message ||
    batchesError?.message ||
    assignmentsError?.message ||
    '';

  const tableLoading =
    assignmentsQueryEnabled && (isAssignmentsLoading || isAssignmentsFetching);

  return {
    activeTab,
    workflowId,
    batchId,
    workflowValue,
    batchValue,
    workflowOptions,
    batchOptions,
    handleTabChange,
    handleWorkflowChange,
    handleBatchChange,
    isWorkflowLoading,
    isWorkflowFetching,
    isBatchesLoading,
    isBatchesFetching,
    rows,
    rowCount,
    tableLoading,
    tableFilters,
    applyTableFiltersToUrl,
    sorting,
    onSortingChange: handleSortingChange,
    pagination,
    setPagination,
    refetchAssignments,
    pageSizeOptions,
    defaultPageSize,
    errorMessage,
  };
}
